import { defineComponent as _defineComponent } from 'vue'
import { computed, watch } from 'vue'
import InfoIcon from '../common/InfoIcon.vue'
// Убираем импорт camelize, так как он не подходит для строк

interface KpiTask {
    id: string
    name: string
    weight: number
    plan: number
    fact: number
    percentage: number
    weighted: number
    type: string
    maxLimitPercentage?: number
    isAtLimit: boolean
    description?: string
}

interface Props {
    shifts: any[]
    plans: any[]
}


export default /*#__PURE__*/_defineComponent({
  __name: 'KpiSummary',
  props: {
    shifts: null,
    plans: null
  },
  emits: ['update-overall-kpi'],
  setup(__props: any, { emit }) {

const props = __props as {
    shifts: any[]
    plans: any[]
};



const formatValue = (value: number, type: string, taskName?: string, plan?: number): string => {
    // Проверяем, есть ли в названии задачи символ "%"
    const hasPercentInName = taskName && taskName.includes('%')

    // Если в названии есть "%" и есть план, отображаем факт как процент от плана
    if (hasPercentInName && plan !== undefined && plan > 0) {
        // Вычисляем процент: (факт / план) * 100
        const numValue = Number(value) || 0
        const numPlan = Number(plan) || 0
        if (numPlan > 0) {
            const percentage = Math.round((numValue / numPlan) * 100)
            return `${percentage}%`
        }
    }

    if (type === 'currency') {
        return `${value.toLocaleString('ru-RU')} P`
    } else if (type === 'decimal') {
        return `${value} Б`
    } else {
        return value.toString()
    }
}

const getWeightedClass = (weighted: number): string => {
    if (weighted >= 100) return 'weighted-green'
    if (weighted >= 95) return 'weighted-normal'
    return 'weighted-red'
}

const kpiTasks = computed((): KpiTask[] => {
    if (!props.shifts.length || !props.plans.length) {
        return []
    }

    const tasks: KpiTask[] = []

    // Получаем все уникальные задачи из планов смен
    const allTasks = new Map()

    props.shifts.forEach(shift => {
        const plan = props.plans.find(p => p.id === shift.planId)
        if (plan && plan.tasks) {
            plan.tasks.forEach((task: any) => {
                if (!allTasks.has(task.id)) {
                    allTasks.set(task.id, {
                        id: task.id,
                        name: task.name,
                        weight: task.significance || 0,
                        type: task.type,
                        description: task.description || '',
                        shifts: [],
                    })
                }
            })
        }
    })

    // Рассчитываем данные для каждой задачи
    allTasks.forEach((taskData, taskId) => {
        let totalPlan = 0
        let totalFact = 0
        let totalWeight = 0
        let shiftCount = 0
        let totalLimitedPercentage = 0 // Сумма процентов с учетом лимитов

        props.shifts.forEach(shift => {
            const plan = props.plans.find(p => p.id === shift.planId)
            if (plan && plan.tasks) {
                const task = plan.tasks.find((t: any) => t.id === taskId)
                if (task && shift.indicators) {
                    const branchId = shift.branchId || 'general'
                    const channelId = shift.channelId || 'general'

                    // Получаем норму
                    let norm = 0
                    const effectiveChannelId = channelId || 'general'

                    if (
                        task.norms &&
                        task.norms[branchId] &&
                        task.norms[branchId][effectiveChannelId] !== undefined
                    ) {
                        norm = task.norms[branchId][effectiveChannelId]
                    } else if (
                        task.norms &&
                        task.norms['general'] &&
                        task.norms['general'][effectiveChannelId] !== undefined
                    ) {
                        norm = task.norms['general'][effectiveChannelId]
                    } else if (
                        task.norms &&
                        task.norms[branchId] &&
                        task.norms[branchId]['general'] !== undefined
                    ) {
                        norm = task.norms[branchId]['general']
                    } else if (
                        task.norms &&
                        task.norms['general'] &&
                        task.norms['general']['general'] !== undefined
                    ) {
                        norm = task.norms['general']['general']
                    }

                    const fact = parseFloat(shift.indicators[taskId]) || 0

                    // Пропускаем задачи с нулевой нормой
                    if (norm === 0) {
                        return
                    }

                    // Получаем максимальный лимит для этой конкретной задачи/смены из branchId и channelId
                    let maxCompletionLimit = 150 // По умолчанию
                    if (
                        task.limits &&
                        task.limits[branchId] &&
                        task.limits[branchId][effectiveChannelId] !== undefined
                    ) {
                        maxCompletionLimit = parseFloat(task.limits[branchId][effectiveChannelId])
                    } else if (
                        task.limits &&
                        task.limits['general'] &&
                        task.limits['general'][effectiveChannelId] !== undefined
                    ) {
                        maxCompletionLimit = parseFloat(task.limits['general'][effectiveChannelId])
                    } else if (
                        task.limits &&
                        task.limits[branchId] &&
                        task.limits[branchId]['general'] !== undefined
                    ) {
                        maxCompletionLimit = parseFloat(task.limits[branchId]['general'])
                    } else if (
                        task.limits &&
                        task.limits['general'] &&
                        task.limits['general']['general'] !== undefined
                    ) {
                        maxCompletionLimit = parseFloat(task.limits['general']['general'])
                    } else if (plan.maxCompletionLimit !== undefined) {
                        // Если у задачи нет своего лимита, берем из плана
                        maxCompletionLimit = parseFloat(plan.maxCompletionLimit)
                    }

                    // Вычисляем процент для этой смены с учетом лимита
                    let shiftPercentage = norm > 0 ? (fact / norm) * 100 : 0
                    shiftPercentage = Math.round(shiftPercentage * 10) / 10 // Округляем до десятых
                    shiftPercentage = Math.min(shiftPercentage, maxCompletionLimit) // Применяем лимит

                    totalPlan += norm
                    totalFact += fact
                    totalWeight += task.significance || 0
                    totalLimitedPercentage += shiftPercentage
                    shiftCount++
                }
            }
        })

        if (shiftCount > 0) {
            const avgWeight = totalWeight / shiftCount

            // Используем средний процент с учетом индивидуальных лимитов каждой смены
            let percentage = totalLimitedPercentage / shiftCount
            percentage = Math.round(percentage * 10) / 10 // Округляем до десятых

            // Вычисляем взвешенное значение с округлением до десятых
            let rawWeighted = (percentage / 100) * avgWeight
            rawWeighted = Math.round(rawWeighted * 10) / 10 // Округляем до десятых
            const weighted = rawWeighted

            // Проверяем, есть ли максимальный предел у задачи
            let maxLimitPercentage: number | undefined
            let isAtLimit = false

            props.shifts.forEach(shift => {
                const plan = props.plans.find(p => p.id === shift.planId)
                if (plan && plan.tasks) {
                    const task = plan.tasks.find((t: any) => t.id === taskId)
                    if (task && task.maxLimit !== undefined && shift.indicators) {
                        const fact = parseFloat(shift.indicators[taskId]) || 0

                        if (fact >= task.maxLimit) {
                            isAtLimit = true
                            // Рассчитываем предел в процентах от нормы
                            const branchId = shift.branch_id || 'general'
                            const channelId = shift.channel_id || 'general'
                            const effectiveChannelId = channelId || 'general'

                            let norm = 0
                            if (
                                task.norms &&
                                task.norms[branchId] &&
                                task.norms[branchId][effectiveChannelId] !== undefined
                            ) {
                                norm = task.norms[branchId][effectiveChannelId]
                            } else if (
                                task.norms &&
                                task.norms['general'] &&
                                task.norms['general'][effectiveChannelId] !== undefined
                            ) {
                                norm = task.norms['general'][effectiveChannelId]
                            } else if (
                                task.norms &&
                                task.norms[branchId] &&
                                task.norms[branchId]['general'] !== undefined
                            ) {
                                norm = task.norms[branchId]['general']
                            } else if (
                                task.norms &&
                                task.norms['general'] &&
                                task.norms['general']['general'] !== undefined
                            ) {
                                norm = task.norms['general']['general']
                            }

                            if (norm > 0) {
                                maxLimitPercentage = Math.round((task.maxLimit / norm) * 100)
                            }
                        }
                    }
                }
            })

            tasks.push({
                id: taskId,
                name: taskData.name,
                weight: Math.round(avgWeight),
                plan: Math.round(totalPlan * 100) / 100,
                fact: Math.round(totalFact * 100) / 100,
                percentage,
                weighted,
                type: taskData.type || '',
                maxLimitPercentage,
                isAtLimit,
                description: taskData.description || '',
            })
        }
    })

    return tasks.sort((a, b) => b.weight - a.weight)
})

const overallKpi = computed((): number => {
    if (!kpiTasks.value.length) return 0

    const totalWeighted = kpiTasks.value.reduce((sum, task) => sum + task.weighted, 0)
    const totalWeight = kpiTasks.value.reduce((sum, task) => sum + task.weight, 0)

    // Вычисляем общий KPI с округлением до десятых
    // Лимиты уже учтены при расчете каждой задачи
    let rawKpi = totalWeight > 0 ? (totalWeighted / totalWeight) * 100 : 0
    rawKpi = Math.round(rawKpi * 10) / 10 // Округляем до десятых

    return rawKpi
})

// Средний процент выполнения всех задач (с учетом веса)
const averagePercentage = computed((): number => {
    if (!kpiTasks.value.length) return 0

    const totalPercentage = kpiTasks.value.reduce((sum, task) => sum + task.percentage, 0)

    let avgPercentage = totalPercentage / kpiTasks.value.length
    avgPercentage = Math.round(avgPercentage * 10) / 10 // Округляем до десятых

    return avgPercentage
})

// Emit overall KPI and average percentage to parent component


watch(
    [overallKpi, averagePercentage],
    ([kpiValue, avgValue]) => {
        emit('update-overall-kpi', { kpi: kpiValue, average: avgValue })
    },
    { immediate: true }
)

return { __sfc: true,props, formatValue, getWeightedClass, kpiTasks, overallKpi, averagePercentage, emit, InfoIcon }
}

})